import React, { useState, useEffect, useCallback } from 'react';
import confetti from 'canvas-confetti';
import { useAuth } from '../context/AuthContext';
import LevelShield from '../components/LevelShield';
import { sounds, preload } from '../utils/sounds';

function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  if (url.includes('/embed/')) return url;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
  if (match) return `https://www.youtube.com/embed/${match[1]}`;
  return url;
}

function toDateOnly(dateStr) {
  return (dateStr || '').split('T')[0];
}

function getLocalToday() {
  const d = new Date();
  return d.getFullYear() + '-' +
    String(d.getMonth() + 1).padStart(2, '0') + '-' +
    String(d.getDate()).padStart(2, '0');
}

function getLocalYesterday() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.getFullYear() + '-' +
    String(d.getMonth() + 1).padStart(2, '0') + '-' +
    String(d.getDate()).padStart(2, '0');
}

function shiftDate(dateStr, days) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.getFullYear() + '-' +
    String(d.getMonth() + 1).padStart(2, '0') + '-' +
    String(d.getDate()).padStart(2, '0');
}

export default function Today() {
  const { apiFetch } = useAuth();
  const [selectedDate, setSelectedDate] = useState(getLocalToday());
  const [drill, setDrill] = useState(null);
  const [completion, setCompletion] = useState(null);
  const [didExtra, setDidExtra] = useState(false);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [streak, setStreak] = useState(null);
  const [celebration, setCelebration] = useState(null);
  const [isPaid, setIsPaid] = useState(true);

  // Quiz state
  const [quizActive, setQuizActive] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizIndex, setQuizIndex] = useState(0);
  const [quizTotalCount, setQuizTotalCount] = useState(0);
  const [quizAnswer, setQuizAnswer] = useState('');
  const [quizSelectedIds, setQuizSelectedIds] = useState([]);
  const [quizSubmitting, setQuizSubmitting] = useState(false);
  const [quizFeedback, setQuizFeedback] = useState(null);
  const [quizPointsEarned, setQuizPointsEarned] = useState(0);
  const [quizComplete, setQuizComplete] = useState(false);
  // Store completion data for celebration after quiz
  const [pendingCelebration, setPendingCelebration] = useState(null);
  const [toast, setToast] = useState(null);
  const [drillLocked, setDrillLocked] = useState(false);

  const today = getLocalToday();
  const yesterday = getLocalYesterday();
  const isToday = selectedDate === today;
  const isFuture = selectedDate > today;

  // Free: today only. Paid: 6-day window (today + 5 back).
  const canComplete = isPaid
    ? (() => { const cutoff = shiftDate(today, -5); return selectedDate >= cutoff && selectedDate <= today; })()
    : selectedDate === today;

  // Free players: can view 1 day back, 2+ days grayed out
  const daysBack = Math.round((new Date(today + 'T00:00:00') - new Date(selectedDate + 'T00:00:00')) / (1000 * 60 * 60 * 24));
  const isGrayedOut = !isPaid && daysBack >= 2;

  const fetchDrill = useCallback(async (date) => {
    setLoading(true);
    try {
      const data = await apiFetch(`/api/drills/date/${date}`);
      setDrill(data.drill);
      setCompletion(data.completion);
      setDrillLocked(data.drill_locked || false);
      setDidExtra(false);
      if (data.completion) {
        const stats = await apiFetch('/api/me/stats');
        setStreak(stats.current_streak);
      } else {
        setStreak(null);
      }
    } catch (err) {
      console.error(err);
      setDrill(null);
      setCompletion(null);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    fetchDrill(selectedDate);
  }, [selectedDate, fetchDrill]);

  // Pre-load sound files so they play instantly
  useEffect(() => { preload(); }, []);
  useEffect(() => { apiFetch('/api/player/is-paid').then(d => setIsPaid(d.isPaid)).catch(() => {}); }, [apiFetch]);


  const canGoBack = isPaid || daysBack < 1;
  const goToPrev = () => { if (canGoBack) setSelectedDate(shiftDate(selectedDate, -1)); };
  const goToNext = () => {
    if (!isToday) setSelectedDate(shiftDate(selectedDate, 1));
  };
  const goToToday = () => setSelectedDate(today);

  const triggerCelebration = (data) => {
    // Always fire base confetti for drill completion
    confetti({
      particleCount: 150,
      spread: 80,
      origin: { y: 0.7 },
      colors: ['#f77c00', '#1348e5', '#ffffff', '#22c55e'],
    });

    // Determine the highest-priority celebration
    let celebrationType = null;
    let celebrationData = {};
    let celebrationDuration = 4000;
    let celebrationSound = null;

    if (data.levelUp) {
      const isPrestige = data.levelUp.isPrestige;
      celebrationType = 'levelUp';
      celebrationData = { level: data.levelUp, isPrestige };
      celebrationDuration = isPrestige ? 6000 : 4000;
      celebrationSound = () => sounds.levelUp(isPrestige);

      confetti({
        particleCount: isPrestige ? 500 : 300,
        spread: 120,
        origin: { y: 0.5 },
        colors: isPrestige
          ? ['#FFD700', '#FFA500', '#ffffff', '#FFE066', '#DAA520']
          : ['#f77c00', '#1348e5', '#FFD700', '#22c55e', '#FF69B4'],
      });
      if (isPrestige) {
        confetti({ particleCount: 100, angle: 60, spread: 55, origin: { x: 0 } });
        confetti({ particleCount: 100, angle: 120, spread: 55, origin: { x: 1 } });
      }
    } else if (data.newBadges && data.newBadges.length > 0) {
      celebrationType = 'badge';
      celebrationData = { count: data.newBadges.length, badges: data.newBadges };
      celebrationSound = () => sounds.badgeEarned();

      const highValueBadges = ['500-club', '1000-club', '2000-club', '3000-club', '5000-club', '10000-club'];
      if (data.newBadges.some(b => highValueBadges.includes(b))) {
        confetti({ particleCount: 250, spread: 100, origin: { y: 0.6 } });
      }
    } else if (data.perfectWeek) {
      celebrationType = 'perfectWeek';
      celebrationSound = () => sounds.perfectWeek();
      confetti({ particleCount: 200, spread: 100, origin: { y: 0.6 },
        colors: ['#FFD700', '#f77c00', '#22c55e'] });
    } else if (data.streakMilestone) {
      celebrationType = 'streakMilestone';
      celebrationData = { days: data.streakMilestone };
      celebrationSound = () => sounds.streakMilestone();
      confetti({ particleCount: Math.min(data.streakMilestone * 10, 300), spread: 90, origin: { y: 0.6 } });
    } else if (data.isFirstCompletion) {
      celebrationType = 'firstCompletion';
      celebrationSound = () => sounds.firstCompletion();
    }

    // Play celebration-specific sound (drill-complete already played on tap)
    if (celebrationSound) {
      celebrationSound();
    }

    // Show celebration overlay
    if (celebrationType) {
      setCelebration({ type: celebrationType, ...celebrationData });
      setTimeout(() => setCelebration(null), celebrationDuration);
    }

    // Leaderboard rank-up toast (separate, fires after main celebration)
    if (data.leaderboardRank) {
      setTimeout(() => {
        setToast({ message: `You moved to #${data.leaderboardRank.after} on the leaderboard!` });
        sounds.leaderboardRankUp();
        setTimeout(() => setToast(null), 3000);
      }, celebrationType ? celebrationDuration + 500 : 500);
    }
  };

  const handleComplete = async () => {
    if (!drill || completing || !canComplete) return;
    // Play drill-complete sound immediately — while still in the
    // user-gesture window — so iOS Safari unlocks audio playback.
    // This also serves as the completion sound itself.
    sounds.drillComplete(didExtra);
    setCompleting(true);
    try {
      const data = await apiFetch(`/api/drills/${drill.id}/complete`, {
        method: 'POST',
        body: JSON.stringify({ did_extra: didExtra }),
      });
      setCompletion(data.completion);

      const stats = await apiFetch('/api/me/stats');
      setStreak(stats.current_streak);

      // Check if drill has questions
      if (drill.has_questions) {
        try {
          const qData = await apiFetch(`/api/drills/${drill.id}/questions`);
          if (qData.questions && qData.questions.length > 0) {
            // Start quiz - defer celebration until after quiz
            setPendingCelebration(data);
            setQuizQuestions(qData.questions);
            setQuizTotalCount(qData.total_count);
            setQuizIndex(0);
            setQuizAnswer('');
            setQuizSelectedIds([]);
            setQuizFeedback(null);
            setQuizPointsEarned(0);
            setQuizComplete(false);
            setQuizActive(true);
            // Fire confetti for drill completion (sound already played above)
            confetti({
              particleCount: 150,
              spread: 80,
              origin: { y: 0.7 },
              colors: ['#f77c00', '#1348e5', '#ffffff', '#22c55e'],
            });
            return;
          }
        } catch (err) {
          console.error('Failed to load questions:', err);
          setToast({ message: 'Could not load quiz questions. You can retry from your profile.' });
          setTimeout(() => setToast(null), 4000);
        }
      }

      // No questions - show celebration immediately
      // (drill-complete sound already played; triggerCelebration will
      // play a celebration-specific sound if there's a milestone)
      triggerCelebration(data);
    } catch (err) {
      if (err.message?.includes('Registration required')) {
        setDrillLocked(true);
      } else {
        console.error(err);
      }
    } finally {
      setCompleting(false);
    }
  };

  const currentQuestion = quizQuestions[quizIndex] || null;

  const handleQuizSubmit = async () => {
    if (!currentQuestion || quizSubmitting) return;
    setQuizSubmitting(true);
    try {
      const body = {};
      if (currentQuestion.input_type === 'text') {
        body.answer = quizAnswer;
      } else {
        body.selected_option_ids = quizSelectedIds;
      }

      const result = await apiFetch(`/api/drills/questions/${currentQuestion.id}/answer`, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (currentQuestion.input_type === 'checkbox') {
        // Show checkbox results immediately
        setQuizFeedback({
          type: 'checkbox_result',
          option_results: result.option_results,
          points_earned: result.points_earned,
        });
        setQuizPointsEarned(prev => prev + result.points_earned);
        if (result.points_earned > 0) sounds.quizCorrect();
      } else if (result.correct) {
        setQuizFeedback({
          type: 'correct',
          points_earned: result.points_earned,
        });
        setQuizPointsEarned(prev => prev + result.points_earned);
        sounds.quizCorrect();
      } else if (result.can_retry) {
        setQuizFeedback({
          type: 'retry',
          message: result.message,
        });
        sounds.quizWrong();
        setQuizAnswer('');
        setQuizSelectedIds([]);
        // Mark the current question as a retry
        const updated = [...quizQuestions];
        updated[quizIndex] = { ...updated[quizIndex], is_retry: true };
        setQuizQuestions(updated);
        // Clear feedback after a short delay so inputs reappear
        setTimeout(() => {
          setQuizFeedback(null);
        }, 1500);
        setQuizSubmitting(false);
        return;
      } else {
        // Wrong on second try - show correct answer
        setQuizFeedback({
          type: 'wrong',
          correct_answers: result.correct_answers,
          points_earned: result.points_earned,
        });
        setQuizPointsEarned(prev => prev + result.points_earned);
        sounds.quizWrong();
      }

      // Auto-advance after delay
      setTimeout(() => {
        advanceQuiz();
      }, 2000);
    } catch (err) {
      console.error(err);
    } finally {
      setQuizSubmitting(false);
    }
  };

  const handleQuizSkip = () => {
    advanceQuiz();
  };

  const advanceQuiz = () => {
    setQuizFeedback(null);
    setQuizAnswer('');
    setQuizSelectedIds([]);
    if (quizIndex + 1 < quizQuestions.length) {
      setQuizIndex(quizIndex + 1);
    } else {
      setQuizComplete(true);
    }
  };

  const finishQuiz = async () => {
    setQuizActive(false);
    setQuizComplete(false);
    // Re-fetch stats to get updated points
    try {
      const stats = await apiFetch('/api/me/stats');
      setStreak(stats.current_streak);
    } catch (err) { console.error(err); }
    // Trigger celebration from drill completion
    if (pendingCelebration) {
      // Re-check badges/level since question points may have changed things
      try {
        const stats = await apiFetch('/api/me/stats');
        setStreak(stats.current_streak);
      } catch (err) { console.error(err); }
      triggerCelebration(pendingCelebration);
      setPendingCelebration(null);
    }
  };

  const toggleCheckboxOption = (optionId) => {
    setQuizSelectedIds(prev =>
      prev.includes(optionId) ? prev.filter(id => id !== optionId) : [...prev, optionId]
    );
  };

  const formatDateHeading = (dateStr) => {
    const d = new Date(toDateOnly(dateStr) + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  };

  const formatDateShort = (dateStr) => {
    const d = new Date(toDateOnly(dateStr) + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getDateLabel = () => {
    if (selectedDate === today) return 'Today';
    if (selectedDate === yesterday) return 'Yesterday';
    return formatDateShort(selectedDate);
  };

  const renderDrillContent = () => {
    if (loading) {
      return <div className="loading"><div className="spinner" /></div>;
    }

    if (!drill) {
      return (
        <div className="rest-day">
          <div className="rest-day-icon">{isFuture ? '...' : '\uD83D\uDE34'}</div>
          <h2>{isFuture ? 'Coming Soon' : 'Rest Day'}</h2>
          <p>{isFuture
            ? 'This drill hasn\'t been posted yet. Check back later!'
            : 'No drill was scheduled for this day.'
          }</p>
        </div>
      );
    }

    if (isGrayedOut) {
      return (
        <div style={{ opacity: 0.35, pointerEvents: 'none' }}>
          <div className="card">
            <div className="card-date">{formatDateHeading(drill.date)}</div>
            <h2 className="card-title">{drill.title}</h2>
            <p className="card-desc">{drill.description}</p>
          </div>
        </div>
      );
    }

    if (completion) {
      return (
        <div className="card">
          <div className="card-date">{formatDateHeading(drill.date)}{drill.is_challenge && <span className="challenge-badge">Challenge Day</span>}</div>
          <h2 className="card-title">{drill.title}</h2>
          <p className="card-desc">{drill.description}</p>
          {drill.target_time && <div className="card-target-time">Target Time: {drill.target_time} min</div>}
          {drill.youtube_url && (
            <div className="video-wrapper">
              <iframe
                src={getYouTubeEmbedUrl(drill.youtube_url)}
                title={drill.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
          <div className="completion-state">
            <div className="completion-check">{'\u2713'}</div>
            <div className="completion-msg">{isToday ? 'You crushed it today!' : 'Completed!'}</div>
            {streak && isToday && <div className="completion-streak">{'\uD83D\uDD25'} {streak} day streak</div>}
            {completion.did_extra && <div className="completion-extra">{'\u2B50'} Extra time logged</div>}
          </div>
          {!completion.did_extra && canComplete && (
            <div style={{ textAlign: 'center', marginTop: 16, padding: '0 16px' }}>
              <p style={{ fontSize: '0.85rem', color: '#ccc', marginBottom: 10 }}>
                If you did your bonus work but failed to claim your bonus points, click below. (Only if you did the bonus work!).
              </p>
              <button
                className="btn btn-outline btn-sm"
                disabled={completing}
                onClick={async () => {
                  setCompleting(true);
                  try {
                    const data = await apiFetch(`/api/drills/${drill.id}/claim-bonus`, { method: 'PUT' });
                    setCompletion(data.completion);
                  } catch (err) { alert(err.message); }
                  finally { setCompleting(false); }
                }}
              >
                {completing ? 'Claiming...' : 'I Did the Bonus Work!'}
              </button>
            </div>
          )}
        </div>
      );
    }

    return (
      <>
        <div className="card">
          <div className="card-date">{formatDateHeading(drill.date)}{drill.is_challenge && <span className="challenge-badge">Challenge Day</span>}</div>
          <h2 className="card-title">{drill.title}</h2>
          <p className="card-desc">{drill.description}</p>
          {drill.target_time && <div className="card-target-time">Target Time: {drill.target_time} min</div>}
          {drill.youtube_url && (
            <div className="video-wrapper" style={drillLocked ? { position: 'relative' } : undefined}>
              <iframe
                src={getYouTubeEmbedUrl(drill.youtube_url)}
                title={drill.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={drillLocked ? { filter: 'grayscale(100%) brightness(0.4)' } : undefined}
              />
              {drillLocked && (
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'not-allowed', zIndex: 2,
                  fontSize: '2.5rem',
                }}>
                  {'\uD83D\uDD12'}
                </div>
              )}
            </div>
          )}
        </div>
        {drillLocked && !completion ? (
          <div style={{ textAlign: 'center', padding: '24px 16px', background: 'rgba(255,255,255,0.05)', borderRadius: 12, margin: '12px 0' }}>
            <div style={{ fontSize: '2rem', marginBottom: 8 }}>{'\uD83D\uDD12'}</div>
            <h3 style={{ color: '#f39c12', marginBottom: 8 }}>Registration Required</h3>
            <p style={{ color: '#aaa', fontSize: '0.9rem' }}>
              Ask your parent to complete registration to keep training every day!
            </p>
          </div>
        ) : canComplete ? (
          <>
            <label className="extra-check">
              <input
                type="checkbox"
                checked={didExtra}
                onChange={(e) => setDidExtra(e.target.checked)}
              />
              <span className="extra-check-label">{drill.bonus_criteria ? `BONUS POINTS: ${drill.bonus_criteria}` : 'I did 15+ extra minutes'}</span>
            </label>
            <div className="mt-12" style={{ textAlign: 'center' }}>
              <button
                className="btn btn-orange did-it-btn"
                onClick={handleComplete}
                disabled={completing}
              >
                {completing ? 'Saving...' : drill.target_time ? `\u26BD I did ${drill.target_time} minutes!` : '\u26BD I did it!'}
              </button>
            </div>
          </>
        ) : (
          <div className="view-only-msg">
            {!isPaid
              ? 'Free accounts can only complete today\'s drill.'
              : 'You can complete drills from the last 6 days.'}
          </div>
        )}
      </>
    );
  };

  const renderQuizOverlay = () => {
    if (!quizActive) return null;

    if (quizComplete) {
      return (
        <div className="quiz-overlay">
          <div className="quiz-card">
            <div className="quiz-complete-emoji">{'\uD83C\uDF89'}</div>
            <div className="quiz-complete-title">Quiz Complete!</div>
            <div className="quiz-complete-points">
              You earned <span className="quiz-points-highlight">+{quizPointsEarned}</span> bonus points
            </div>
            <button className="btn btn-orange" style={{ width: '100%', marginTop: 20 }} onClick={finishQuiz}>Continue</button>
          </div>
        </div>
      );
    }

    if (!currentQuestion) return null;

    const questionNumber = quizIndex + 1;

    return (
      <div className="quiz-overlay">
        <div className="quiz-card">
          <div className="quiz-progress">Question {questionNumber} of {quizQuestions.length}</div>
          <div className="quiz-question-text">{currentQuestion.question_text}</div>
          {currentQuestion.is_retry && !quizFeedback && (
            <div className="quiz-retry-hint">Second attempt - half points</div>
          )}

          {/* Text input */}
          {currentQuestion.input_type === 'text' && !quizFeedback && (
            <input
              className="form-input quiz-input"
              value={quizAnswer}
              onChange={(e) => setQuizAnswer(e.target.value)}
              placeholder="Type your answer..."
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleQuizSubmit(); } }}
            />
          )}

          {/* Radio options */}
          {currentQuestion.input_type === 'radio' && !quizFeedback && (
            <div className="quiz-options">
              {currentQuestion.options.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  className={`quiz-option ${quizSelectedIds.includes(opt.id) ? 'selected' : ''}`}
                  onClick={() => setQuizSelectedIds([opt.id])}
                >
                  <span className="quiz-option-radio">{quizSelectedIds.includes(opt.id) ? '\u25C9' : '\u25CB'}</span>
                  {opt.option_text}
                </button>
              ))}
            </div>
          )}

          {/* Checkbox options */}
          {currentQuestion.input_type === 'checkbox' && !quizFeedback && (
            <div className="quiz-options">
              {currentQuestion.options.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  className={`quiz-option ${quizSelectedIds.includes(opt.id) ? 'selected' : ''}`}
                  onClick={() => toggleCheckboxOption(opt.id)}
                >
                  <span className="quiz-option-check">{quizSelectedIds.includes(opt.id) ? '\u2611' : '\u2610'}</span>
                  {opt.option_text}
                </button>
              ))}
            </div>
          )}

          {/* Feedback */}
          {quizFeedback && quizFeedback.type === 'correct' && (
            <div className="quiz-feedback correct">
              <div className="quiz-feedback-icon">{'\u2713'}</div>
              <div className="quiz-feedback-text">Correct! +{quizFeedback.points_earned} pts</div>
            </div>
          )}

          {quizFeedback && quizFeedback.type === 'retry' && (
            <div className="quiz-feedback retry">
              <div className="quiz-feedback-text">{quizFeedback.message}</div>
            </div>
          )}

          {quizFeedback && quizFeedback.type === 'wrong' && (
            <div className="quiz-feedback wrong">
              <div className="quiz-feedback-text">
                The correct answer{quizFeedback.correct_answers.length > 1 ? 's' : ''}: {quizFeedback.correct_answers.join(', ')}
              </div>
              {quizFeedback.points_earned > 0 && (
                <div className="quiz-feedback-points">+{quizFeedback.points_earned} pts (half credit)</div>
              )}
            </div>
          )}

          {quizFeedback && quizFeedback.type === 'checkbox_result' && (
            <div className="quiz-feedback checkbox-result">
              {quizFeedback.option_results.map((opt) => (
                <div key={opt.id} className={`quiz-result-row ${opt.is_correct && opt.was_selected ? 'correct' : opt.was_selected && !opt.is_correct ? 'wrong-pick' : opt.is_correct ? 'missed' : ''}`}>
                  <span className="quiz-result-icon">
                    {opt.was_selected && opt.is_correct ? '\u2713' : opt.was_selected && !opt.is_correct ? '\u2717' : opt.is_correct ? '\u25CB' : ''}
                  </span>
                  <span className="quiz-result-text">{opt.option_text}</span>
                  {opt.points_earned > 0 && <span className="quiz-result-points">+{opt.points_earned}</span>}
                </div>
              ))}
              <div className="quiz-feedback-points" style={{ marginTop: 8 }}>+{quizFeedback.points_earned} pts total</div>
            </div>
          )}

          {/* Actions */}
          {!quizFeedback && (
            <div className="quiz-actions">
              <button type="button" className="btn btn-outline" onClick={handleQuizSkip}>Skip</button>
              <button
                type="button"
                className="btn btn-orange"
                onClick={handleQuizSubmit}
                disabled={quizSubmitting || (currentQuestion.input_type === 'text' ? !quizAnswer.trim() : quizSelectedIds.length === 0)}
              >
                {quizSubmitting ? 'Checking...' : 'Submit'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="page">
      <div className="date-nav">
        <button className="date-nav-btn" onClick={goToPrev} disabled={!canGoBack}>{'\u2039'}</button>
        <button className="date-nav-label" onClick={goToToday}>
          {getDateLabel()}
        </button>
        <button className="date-nav-btn" onClick={goToNext} disabled={isToday}>{'\u203A'}</button>
      </div>
      {renderDrillContent()}

      {renderQuizOverlay()}

      {celebration && (
        <div className={`celebration-overlay${celebration.type === 'levelUp' && celebration.isPrestige ? ' prestige' : ''}`} onClick={() => setCelebration(null)}>
          <div className={`celebration-card${celebration.type === 'levelUp' && celebration.isPrestige ? ' prestige-card' : ''}`}>
            {celebration.type === 'levelUp' ? (
              <>
                <LevelShield
                  name={celebration.level.name}
                  color={celebration.level.color}
                  textColor={celebration.level.textColor}
                  isPrestige={celebration.level.isPrestige}
                  subtitle={celebration.level.subtitle}
                  size="large"
                />
                <div className="celebration-title">{celebration.isPrestige ? 'PRESTIGE Level Up!' : 'Level Up!'}</div>
                <div className="celebration-msg">You reached {celebration.level.name}!</div>
              </>
            ) : celebration.type === 'badge' ? (
              <>
                <div className="celebration-emoji">{'\uD83C\uDFC5'}</div>
                <div className="celebration-title">Badge Earned!</div>
                <div className="celebration-msg">
                  You earned {celebration.count} new badge{celebration.count > 1 ? 's' : ''}! Check your profile.
                </div>
              </>
            ) : celebration.type === 'perfectWeek' ? (
              <>
                <div className="celebration-emoji">{'\uD83D\uDCC5'}</div>
                <div className="celebration-title">Perfect Week!</div>
                <div className="celebration-msg">You completed every drill this week. Outstanding dedication!</div>
              </>
            ) : celebration.type === 'streakMilestone' ? (
              <>
                <div className="celebration-emoji">{'\uD83D\uDD25'}</div>
                <div className="celebration-title">{celebration.days}-Day Streak!</div>
                <div className="celebration-msg">
                  {celebration.days === 3 ? "You're building momentum!" :
                   celebration.days === 7 ? "A full week! You're on fire!" :
                   celebration.days === 14 ? "Two weeks strong! Incredible discipline!" :
                   celebration.days === 21 ? "Three weeks! You're unstoppable!" :
                   "Absolutely legendary!"}
                </div>
              </>
            ) : celebration.type === 'firstCompletion' ? (
              <>
                <div className="celebration-emoji">{'\u26BD'}</div>
                <div className="celebration-title">Welcome to the Team!</div>
                <div className="celebration-msg">Your journey begins! Complete drills every day to build your streak.</div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {toast && (
        <div className="toast-notification">
          <div className="toast-content">
            {'\uD83D\uDCC8'} {toast.message}
          </div>
        </div>
      )}
    </div>
  );
}
